import { useState, useRef, useCallback, useEffect } from 'react';
import { mediaStreamStore } from 'stores/mediaStreamStore';

interface UseVoiceActivityResult {
  isVoiceActive: boolean;
  voiceVolume: number;
  startAnalysis: (stream: MediaStream) => void;
  startAnalysisWithNewStream: () => Promise<void>;
  stopAnalysis: () => void;
}

const VOICE_THRESHOLD = 0.02;
// epsilon: пропускаем setState, если громкость не изменилась заметно.
// LinearProgress в VoiceActivityDetector рисует volume*300 → 0.005 ≈ 1.5 пикселя, незаметно
const VOLUME_EPSILON = 0.005;

export const useVoiceActivity = (): UseVoiceActivityResult => {
  const [isVoiceActive, setIsVoiceActive] = useState(false);
  const [voiceVolume, setVoiceVolume] = useState(0);

  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const audioStreamRef = useRef<MediaStream | null>(null);
  const lastVolumeRef = useRef(0);
  const lastIsActiveRef = useRef(false);

  // Функция анализа аудио активности
  const analyzeAudioActivity = useCallback(() => {
    if (!analyserRef.current || animationFrameRef.current === null) return;

    const bufferLength = analyserRef.current.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    analyserRef.current.getByteFrequencyData(dataArray);

    const average = dataArray.reduce((sum, value) => sum + value, 0) / bufferLength;
    const normalizedVolume = average / 255;

    if (Math.abs(normalizedVolume - lastVolumeRef.current) >= VOLUME_EPSILON) {
      lastVolumeRef.current = normalizedVolume;
      setVoiceVolume(normalizedVolume);
    }

    const isActive = normalizedVolume > VOICE_THRESHOLD;
    if (isActive !== lastIsActiveRef.current) {
      lastIsActiveRef.current = isActive;
      setIsVoiceActive(isActive);
    }

    animationFrameRef.current = requestAnimationFrame(analyzeAudioActivity);
  }, []);

  const startAnalysis = useCallback(
    (stream: MediaStream) => {
      // Настраиваем анализ аудио
      try {
        const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
        const analyser = audioContext.createAnalyser();
        const source = audioContext.createMediaStreamSource(stream);

        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.8;
        source.connect(analyser);

        audioContextRef.current = audioContext;
        analyserRef.current = analyser;

        // Запускаем анализ аудио
        animationFrameRef.current = requestAnimationFrame(analyzeAudioActivity);
      } catch (error) {
        console.warn('Не удалось настроить анализ аудио:', error);
      }
    },
    [analyzeAudioActivity]
  );

  const startAnalysisWithNewStream = useCallback(async () => {
    // Останавливаем предыдущий поток, если есть
    if (audioStreamRef.current) {
      mediaStreamStore.removeReference();
      audioStreamRef.current = null;
    }

    try {
      // Пробуем получить аудио стрим из глобального store
      const currentStream = mediaStreamStore.getCurrentStream();
      if (currentStream && currentStream.getAudioTracks().length > 0) {
        audioStreamRef.current = currentStream;
        mediaStreamStore.addReference();
        startAnalysis(currentStream);
        return;
      }

      // Если глобального стрима нет, создаем новый через store
      const audioStream = await mediaStreamStore.getAudioStream();
      if (audioStream) {
        audioStreamRef.current = audioStream;
        mediaStreamStore.addReference();
        startAnalysis(audioStream);
      }
    } catch (err) {
      console.warn('Не удалось запустить анализ аудио:', err);
    }
  }, [startAnalysis]);

  const stopAnalysis = useCallback(() => {
    // Останавливаем анализ аудио
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    // Очищаем аудио контекст
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    analyserRef.current = null;

    // Освобождаем ссылку на стрим в store
    if (audioStreamRef.current) {
      mediaStreamStore.removeReference();
      audioStreamRef.current = null;
    }

    // Сбрасываем состояние голоса
    lastVolumeRef.current = 0;
    lastIsActiveRef.current = false;
    setIsVoiceActive(false);
    setVoiceVolume(0);
  }, []);

  // Очистка при размонтировании
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
      analyserRef.current = null;
      if (audioStreamRef.current) {
        mediaStreamStore.removeReference();
        audioStreamRef.current = null;
      }
    };
  }, []);

  return {
    isVoiceActive,
    voiceVolume,
    startAnalysis,
    startAnalysisWithNewStream,
    stopAnalysis
  };
};

